/**
 * Review-queue shared core (v3 homepage).
 *
 * The cross-user review-queue endpoint (`POST Report/reviewQueue`) backs THREE
 * widgets — TaskTable, AdminPriority and AttentionQueue — that present the same
 * rows differently. This module holds what they share: the fetch, the canonical
 * `status_key` → presentation map, and the per-row derivations (anomaly /
 * overdue / owner / level text). Each widget's composable layers its own
 * ranking/filtering/shaping on top. Pure helpers (no Vue state) so they compose
 * freely; `http` is injected by the caller.
 *
 * Row shape (from getReviewQueue): { doc_id, reference, doc_name, doc_title,
 *   tmpl_id, lifecycle_status_name, lifecycle_status_colour, lifecycle_status_position,
 *   document_lf_evaluation_id, evaluation_status, owner_user_id, owner_name,
 *   submitted_at, selected_level, suggested_level, selected_position,
 *   suggested_position, is_anomaly, due_date, overdue, status_key }.
 */

// status_key → { label, colour token, tone (icon-chip class), icon, action }.
// Lifecycle status names are client-configured; the endpoint maps them to these
// canonical keys (see getReviewQueue). `tone` matches the `.v3-ic--*` classes.
export const RQ_STATUS_META = {
  breach: { label: 'Anomaly', colour: 'var(--v3-attn, #C8447C)', tone: 'rose', icon: 'flag', action: 'Review' },
  overdue: { label: 'Overdue', colour: 'var(--v3-warn, #B27B12)', tone: 'amber', icon: 'warning', action: 'Chase' },
  'pending-approval': { label: 'Needs approval', colour: 'var(--v3-accent)', tone: 'accent', icon: 'how_to_reg', action: 'Approve' },
  're-review': { label: 'Revised', colour: 'var(--v3-revise, #6D5AE6)', tone: 'violet', icon: 'undo', action: 'Review' },
  'pending-review': { label: 'Awaiting review', colour: 'var(--v3-accent)', tone: 'accent', icon: 'rate_review', action: 'Review' },
  approved: { label: 'Approved', colour: 'var(--success-fg, #1F7A53)', tone: 'green', icon: 'check_circle', action: 'View' },
};
export const RQ_FALLBACK_META = { label: '', colour: '#5A6678', tone: 'neutral', icon: 'radio_button_unchecked', action: 'Review' };

export const rqMeta = (r) => RQ_STATUS_META[r && r.status_key] || RQ_FALLBACK_META;
export const rqIsAnomaly = (r) => !!r && (r.is_anomaly === true || r.is_anomaly === 1 || r.status_key === 'breach');
export const rqIsOverdue = (r) => !!r && (r.overdue === true || r.overdue === 1);
export const rqDocName = (r) => (r && (r.doc_title || r.doc_name || r.reference)) || '';
export const rqOwnerName = (r) => (r && r.owner_name ? String(r.owner_name).trim() : '');
export const rqInitials = (name) => {
  const parts = String(name || '').trim().split(/\s+/);
  if (!parts[0]) return '–';
  return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};
export const rqSelectedLevel = (r) => (r && r.selected_level ? String(r.selected_level) : '');
export const rqSuggestedLevel = (r) => (r && r.suggested_level ? String(r.suggested_level) : '');

// The level outcome + the anomaly criterion when they disagree:
//   anomaly → "Recommended 9, levelled at 3" (system suggestion vs final level)
//   normal  → "Levelled at 3"
export const rqLevelText = (r) => {
  const sel = rqSelectedLevel(r);
  const sug = rqSuggestedLevel(r);
  if (rqIsAnomaly(r) && sug && sel) return `Recommended ${sug}, levelled at ${sel}`;
  if (sel) return `Levelled at ${sel}`;
  if (sug) return `Recommended ${sug}`;
  return '';
};

export const rqFmtDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Fetch the review queue. Tolerates a bare array or a `{ Data: [] }` envelope. */
export async function fetchReviewQueue(http, { scope = 'all', limit = 200 } = {}) {
  const resp = await http.post('Report/reviewQueue', { scope, limit });
  const data = resp && resp.data;
  return Array.isArray(data) ? data : data && Array.isArray(data.Data) ? data.Data : [];
}
